<template>
  <div class="ride-manager-billing">
    <NavBar />
    
    <div class="container-fluid py-4">
      <div class="row">
        <div class="col-12">
          <div class="card shadow">
            <div class="card-header bg-white border-0">
              <div class="row align-items-center">
                <div class="col">
                  <h3 class="mb-0">Ride Manager Billing</h3>
                  <p class="text-muted mb-0">View billing information for all ride managers</p>
                </div>
                <div class="col text-right">
                  <b-button
                    variant="primary"
                    size="sm"
                    @click="refreshBillingData"
                    :disabled="loading"
                    class="mr-2"
                  >
                    <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
                    Refresh
                  </b-button>
                </div>
              </div>
            </div>
            
            <div class="card-body">
              <!-- Loading State -->
              <div v-if="loading" class="text-center py-5">
                <b-spinner variant="primary" />
                <p class="mt-2">Loading billing data...</p>
              </div>
              
              <!-- Error State -->
              <div v-else-if="error" class="text-center py-5">
                <i class="fas fa-exclamation-triangle text-danger fa-3x mb-3"></i>
                <h5 class="text-danger">Error Loading Billing Data</h5>
                <p class="text-muted">{{ error }}</p>
                <b-button variant="primary" @click="fetchBillingData">
                  Try Again
                </b-button>
              </div>
              
              <!-- No Data State -->
              <div v-else-if="!billingData || billingData.length === 0" class="text-center py-5">
                <i class="fas fa-chart-line text-muted fa-3x mb-3"></i>
                <h5 class="text-muted">No Billing Data Available</h5>
                <p class="text-muted">No ride managers found or no billing information available.</p>
              </div>
              
              <!-- Billing Data Display -->
              <div v-else>
                <!-- Summary Cards -->
                <div class="row mb-4">
                  <div class="col-md-3">
                    <div class="card bg-primary text-white">
                      <div class="card-body">
                        <div class="d-flex align-items-center">
                          <div class="flex-grow-1">
                            <h6 class="card-title text-uppercase mb-1">Total Managers</h6>
                            <h3 class="mb-0">{{ billingData.length }}</h3>
                          </div>
                          <div class="ml-3">
                            <i class="fas fa-users fa-2x opacity-75"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="col-md-3">
                    <div class="card bg-info text-white">
                      <div class="card-body">
                        <div class="d-flex align-items-center">
                          <div class="flex-grow-1">
                            <h6 class="card-title text-uppercase mb-1">Total Rides</h6>
                            <h3 class="mb-0">{{ totalRides }}</h3>
                          </div>
                          <div class="ml-3">
                            <i class="fas fa-route fa-2x opacity-75"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="col-md-3">
                    <div class="card bg-warning text-white">
                      <div class="card-body">
                        <div class="d-flex align-items-center">
                          <div class="flex-grow-1">
                            <h6 class="card-title text-uppercase mb-1">Total Shares</h6>
                            <h3 class="mb-0">{{ totalShares }}</h3>
                          </div>
                          <div class="ml-3">
                            <i class="fas fa-share-alt fa-2x opacity-75"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="col-md-3">
                    <div class="card bg-success text-white">
                      <div class="card-body">
                        <div class="d-flex align-items-center">
                          <div class="flex-grow-1">
                            <h6 class="card-title text-uppercase mb-1">Total PDF Exports</h6>
                            <h3 class="mb-0">{{ totalPdfExports }}</h3>
                          </div>
                          <div class="ml-3">
                            <i class="fas fa-file-pdf fa-2x opacity-75"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Manager Billing Details -->
                <div class="row">
                  <div class="col-12">
                    <h5 class="mb-3">Manager Billing Details</h5>
                    <div class="table-responsive">
                      <table class="table table-hover">
                        <thead class="thead-light">
                          <tr>
                            <th>Manager</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Referred By</th>
                            <th>Total Rides</th>
                            <th>Total Shares</th>
                            <th>Total Days</th>
                            <th>PDF Exports</th>
                            <th>First Ride</th>
                            <th>Last Ride</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="manager in billingData" :key="manager.manager_id">
                            <td>
                              <div class="d-flex align-items-center">
                                <div class="avatar-circle bg-primary text-white mr-3">
                                  {{ manager.username ? manager.username.charAt(0).toUpperCase() : 'M' }}
                                </div>
                                <div>
                                  <strong>{{ manager.username || 'Unknown Manager' }}</strong>
                                  <br>
                                  <small class="text-muted">ID: {{ manager.manager_id }}</small>
                                </div>
                              </div>
                            </td>
                            <td>{{ manager.email || 'N/A' }}</td>
                            <td>{{ manager.phone || 'N/A' }}</td>
                            <td>
                              <div v-if="manager.referrer_id">
                                <strong>{{ manager.referrer_username || 'Unknown' }}</strong>
                                <br>
                                <small class="text-muted">{{ manager.referrer_email }}</small>
                                <br>
                                <small class="text-muted">ID: {{ manager.referrer_id }}</small>
                              </div>
                              <span v-else class="text-muted">-</span>
                            </td>
                            <td>
                              <span class="badge badge-info">
                                {{ manager.total_rides || 0 }}
                              </span>
                            </td>
                            <td>
                              <span class="badge badge-warning">
                                {{ manager.total_shares || 0 }}
                              </span>
                            </td>
                            <td>
                              <span class="badge badge-secondary">
                                {{ manager.total_days || 0 }}
                              </span>
                            </td>
                            <td>
                              <span class="badge badge-success">
                                {{ getTotalPdfExportsForManager(manager.manager_id) }}
                              </span>
                            </td>
                            <td>
                              <span v-if="getFirstRideDate(manager)" class="text-muted small">
                                {{ formatDate(getFirstRideDate(manager)) }}
                              </span>
                              <span v-else class="text-muted">N/A</span>
                            </td>
                            <td>
                              <span v-if="getLastRideDate(manager)" class="text-muted small">
                                {{ formatDate(getLastRideDate(manager)) }}
                              </span>
                              <span v-else class="text-muted">N/A</span>
                            </td>
                            <td>
                              <b-button
                                variant="outline-primary"
                                size="sm"
                                @click="viewManagerDetails(manager)"
                              >
                                <i class="fas fa-eye"></i>
                                View Details
                              </b-button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Manager Details Modal -->
    <b-modal
      v-model="showManagerModal"
      :title="`Billing Details - ${selectedManager?.username || 'Manager'}`"
      size="xl"
      dialog-class="modal-xl-custom"
      @hide="selectedManager = null; selectedManagerPdfExports = []; selectedManagerPdfData = null; managerPdfExportError = null; expandedRideId = null;"
    >
      <div v-if="selectedManager" class="billing-modal-body">
        <div class="row mb-3">
          <div class="col-md-4">
            <strong>Manager ID:</strong> {{ selectedManager.manager_id }}
          </div>
          <div class="col-md-4">
            <strong>Email:</strong> {{ selectedManager.email || 'N/A' }}
          </div>
          <div class="col-md-4">
            <strong>Phone:</strong> {{ selectedManager.phone || 'N/A' }}
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-12">
            <div v-if="selectedManager.referrer_id" class="alert alert-success py-2">
              <strong><i class="fas fa-user-friends mr-2"></i>Referred By:</strong>
              {{ selectedManager.referrer_username || 'Unknown' }} &nbsp;|&nbsp;
              {{ selectedManager.referrer_email }} &nbsp;|&nbsp;
              ID: {{ selectedManager.referrer_id }}
            </div>
            <div v-else class="alert alert-secondary py-2">
              <strong><i class="fas fa-user-friends mr-2"></i>Referred By:</strong>
              <span class="text-muted">No referrer</span>
            </div>
          </div>
        </div>
        
        <div class="row mb-3">
          <div class="col-md-3">
            <strong>Total Rides:</strong> {{ selectedManager.total_rides || 0 }}
          </div>
          <div class="col-md-3">
            <strong>Total Shares:</strong> {{ selectedManager.total_shares || 0 }}
          </div>
          <div class="col-md-3">
            <strong>Total Days:</strong> {{ selectedManager.total_days || 0 }}
          </div>
          <div class="col-md-3">
            <strong>PDF Exports:</strong> {{ getTotalPdfExportsForSelectedManager() }}
          </div>
        </div>
        
        <!-- PDF Export Summary from API -->
        <div v-if="selectedManagerPdfData" class="row mb-3">
          <div class="col-12">
            <div class="alert alert-info">
              <div class="row">
                <div class="col-md-4">
                  <strong>Total Rides with PDF Exports:</strong> {{ selectedManagerPdfData.total_rides || 0 }}
                </div>
                <div class="col-md-4">
                  <strong>Total PDF Exports:</strong> {{ selectedManagerPdfData.total_pdf_exports || 0 }}
                </div>
                <div class="col-md-4">
                  <strong>Manager:</strong> {{ selectedManagerPdfData.username || 'N/A' }}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <h6 class="mt-4 mb-3">Ride Details:</h6>
        <div v-if="getManagerRides(selectedManager) && getManagerRides(selectedManager).length > 0">
          <div class="table-responsive">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Ride ID</th>
                  <th>Title</th>
                  <th>Days</th>
                  <th>Shares</th>
                  <th>First Shared</th>
                  <th>Last Shared</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="ride in getManagerRides(selectedManager)" :key="ride.ride_id">
                  <td>
                    <b-badge variant="secondary">{{ ride.ride_id }}</b-badge>
                  </td>
                  <td>
                    <strong>{{ ride.title || 'Untitled Ride' }}</strong>
                  </td>
                  <td>
                    <b-badge variant="info">{{ ride.number_of_days || 0 }}</b-badge>
                  </td>
                  <td>
                    <b-badge variant="warning">{{ ride.number_of_shares || 0 }}</b-badge>
                  </td>
                  <td>
                    <span v-if="ride.first_shared_date" class="text-success">
                      {{ formatDate(ride.first_shared_date) }}
                    </span>
                    <span v-else class="text-muted">Never</span>
                  </td>
                  <td>
                    <span v-if="ride.last_shared_date" class="text-success">
                      {{ formatDate(ride.last_shared_date) }}
                    </span>
                    <span v-else class="text-muted">Never</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else class="text-center py-3">
          <p class="text-muted">No ride details available</p>
        </div>
        
        <!-- PDF Export Information -->
        <h6 class="mt-4 mb-3">PDF Export History:</h6>
        <div v-if="loadingManagerPdfInfo" class="text-center py-3">
          <b-spinner small variant="primary" />
          <p class="mt-2 small text-muted">Loading PDF export information...</p>
        </div>
        <div v-else-if="managerPdfExportError" class="alert alert-warning">
          <small>{{ managerPdfExportError }}</small>
        </div>
        <div v-else-if="selectedManagerPdfExports && selectedManagerPdfExports.length > 0">
          <div class="table-responsive">
            <table class="table table-sm table-bordered">
              <thead class="thead-light">
                <tr>
                  <th>Ride ID</th>
                  <th>Ride Title</th>
                  <th>Days</th>
                  <th>Shares</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Number of Exports</th>
                  <th>First Export</th>
                  <th>Last Export</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="pdfExport in selectedManagerPdfExports">
                  <tr :key="pdfExport.ride_id">
                    <td>
                      <b-badge variant="info">{{ pdfExport.ride_id }}</b-badge>
                    </td>
                    <td>
                      <strong>{{ pdfExport.title || 'Untitled Ride' }}</strong>
                      <br v-if="pdfExport.has_variations">
                      <b-badge v-if="pdfExport.has_variations" variant="warning" class="mt-1">
                        <i class="fas fa-exclamation-triangle mr-1"></i>Data changed between exports
                      </b-badge>
                    </td>
                    <td>
                      <b-badge variant="info">{{ getRideStatsFromManager(pdfExport.ride_id).number_of_days }}</b-badge>
                    </td>
                    <td>
                      <b-badge variant="warning">{{ getRideStatsFromManager(pdfExport.ride_id).number_of_shares }}</b-badge>
                    </td>
                    <td>
                      <span v-if="pdfExport.start_date" class="text-success">
                        {{ formatDate(pdfExport.start_date) }}
                      </span>
                      <span v-else class="text-muted">N/A</span>
                    </td>
                    <td>
                      <span v-if="pdfExport.end_date" class="text-success">
                        {{ formatDate(pdfExport.end_date) }}
                      </span>
                      <span v-else class="text-muted">N/A</span>
                    </td>
                    <td>
                      <b-badge variant="warning">{{ pdfExport.number_of_pdf_exports || 0 }}</b-badge>
                    </td>
                    <td>
                      <span v-if="pdfExport.first_pdf_export_date" class="text-primary">
                        {{ formatDate(pdfExport.first_pdf_export_date, true) }}
                      </span>
                      <span v-else class="text-muted">N/A</span>
                    </td>
                    <td>
                      <span v-if="pdfExport.last_pdf_export_date" class="text-primary">
                        {{ formatDate(pdfExport.last_pdf_export_date, true) }}
                      </span>
                      <span v-else class="text-muted">N/A</span>
                    </td>
                    <td>
                      <b-button
                        variant="link"
                        size="sm"
                        @click="expandedRideId = expandedRideId === pdfExport.ride_id ? null : pdfExport.ride_id"
                      >
                        <i :class="expandedRideId === pdfExport.ride_id ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                        {{ expandedRideId === pdfExport.ride_id ? 'Hide' : 'Show' }} Details
                      </b-button>
                    </td>
                  </tr>
                  <!-- Expanded row showing detailed PDF exports -->
                  <tr :key="pdfExport.ride_id + '-details'" v-if="expandedRideId === pdfExport.ride_id">
                    <td colspan="10">
                      <div class="pl-4 pr-4 pb-3">
                        <h6 class="mb-2">Detailed PDF Export Records:</h6>
                        <div class="table-responsive">
                          <table class="table table-sm table-bordered mb-0">
                            <thead class="thead-light">
                              <tr>
                                <th>Export ID</th>
                                <th>Ride Title (at export time)</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Days</th>
                                <th>Exported At</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="exportRecord in pdfExport.pdf_exports" :key="exportRecord.id">
                                <td>
                                  <b-badge variant="secondary">{{ exportRecord.id }}</b-badge>
                                </td>
                                <td>{{ exportRecord.ride_title || 'N/A' }}</td>
                                <td>
                                  <span v-if="exportRecord.ride_start_date" class="text-success">
                                    {{ formatDate(exportRecord.ride_start_date) }}
                                  </span>
                                  <span v-else class="text-muted">N/A</span>
                                </td>
                                <td>
                                  <span v-if="exportRecord.ride_end_date" class="text-success">
                                    {{ formatDate(exportRecord.ride_end_date) }}
                                  </span>
                                  <span v-else class="text-muted">N/A</span>
                                </td>
                                <td>
                                  <b-badge variant="info">{{ exportRecord.number_of_days || 'N/A' }}</b-badge>
                                </td>
                                <td>
                                  <span class="text-primary">
                                    {{ formatDate(exportRecord.created_at, true) }}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          <div class="mt-2">
            <small class="text-muted">
              Total PDF Exports: <strong>{{ getTotalPdfExportsForSelectedManager() }}</strong>
            </small>
          </div>
        </div>
        <div v-else class="text-center py-3">
          <p class="text-muted">No PDF export history available</p>
        </div>
      </div>
    </b-modal>
    
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import NavBar from '@/components/NavBar.vue';
import rideInformationAPI from '@/api/rideInformation';

import { INTERNAL_HOME_PATH } from '@/common/appHome';
export default {
  name: 'RideManagerBilling',
  
  components: {
    NavBar
  },
  
  data() {
    return {
      loading: false,
      error: null,
      billingData: [],
      allRides: [], // Store all rides if they come at the top level
      selectedManager: null,
      showManagerModal: false,
      pdfExportInfo: [], // Store PDF export information (all managers)
      selectedManagerPdfExports: [], // Store PDF exports rides for selected manager
      selectedManagerPdfData: null, // Store full PDF export manager data
      expandedRideId: null, // Track which ride's PDF exports are expanded
      loadingPdfInfo: false,
      loadingManagerPdfInfo: false,
      pdfExportError: null,
      managerPdfExportError: null
    };
  },
  
  computed: {
    ...mapGetters([
      'isUserManager',
      'apptoken'
    ]),
    
    totalRides() {
      if (!this.billingData || this.billingData.length === 0) return 0;
      return this.billingData.reduce((total, manager) => total + (manager.total_rides || 0), 0);
    },
    
    totalShares() {
      if (!this.billingData || this.billingData.length === 0) return 0;
      return this.billingData.reduce((total, manager) => total + (manager.total_shares || 0), 0);
    },
    
    totalPdfExports() {
      if (!this.pdfExportInfo || !Array.isArray(this.pdfExportInfo)) return 0;
      // The new API structure returns manager objects with total_pdf_exports
      return this.pdfExportInfo.reduce((total, manager) => total + (manager.total_pdf_exports || 0), 0);
    }
  },
  
  created() {
    if (!this.isUserManager) {
      this.$router.push(INTERNAL_HOME_PATH);
      return;
    }
    
    this.fetchBillingData();
    this.fetchPdfExportInfo();
  },
  
  methods: {
    async fetchBillingData() {
      this.loading = true;
      this.error = null;
      
        try {
          const response = await rideInformationAPI.getBillingData(this.apptoken);
          console.log(response)
          if (response.data && response.data.ServerMassageCode === 'success') {
            this.billingData = response.data.billing_data || [];
            
            // Check if rides are at the top level
            if (response.data.rides && Array.isArray(response.data.rides)) {
              this.allRides = response.data.rides;
            }
            
          } else {
            this.error = response.data?.message || 'Failed to load billing data';
          }
        } catch (error) {
          console.error('Failed to fetch billing data:', error);
          this.error = error.response?.data?.message || 'Failed to load billing data';
        }
      
      this.loading = false;
    },
    
    async refreshBillingData() {
      await this.fetchBillingData();
      await this.fetchPdfExportInfo();
    },
    
    async fetchPdfExportInfo() {
      this.loadingPdfInfo = true;
      this.pdfExportError = null;
      
      try {
        const response = await rideInformationAPI.getPdfExportInfo(this.apptoken);
        // console.log("PDF Export Info for all Manager", managerId, ":", response);
        console.log("the input is :", response )
        if (response.data && response.data.ServerMassageCode === 'success') {
          this.pdfExportInfo = response.data.pdf_export_info || [];
        } else {
          this.pdfExportError = response.data?.message || 'Failed to load PDF export information';
        }
      } catch (error) {
        console.error('Failed to fetch PDF export info:', error);
        this.pdfExportError = error.response?.data?.message || 'Failed to load PDF export information';
      }
      
      this.loadingPdfInfo = false;
    },
    
    getPdfExportsForManager(managerId) {
      if (!this.pdfExportInfo || !Array.isArray(this.pdfExportInfo)) return [];
      if (!managerId) return [];
      
      // The new API structure returns manager objects with manager_id, rides, total_pdf_exports
      // Find the manager object that matches the managerId
      const managerPdfData = this.pdfExportInfo.find(manager => manager.manager_id === managerId);
      
      if (managerPdfData && managerPdfData.rides && Array.isArray(managerPdfData.rides)) {
        return managerPdfData.rides;
      }
      
      return [];
    },
    
    getTotalPdfExportsForManager(managerId) {
      if (!this.pdfExportInfo || !Array.isArray(this.pdfExportInfo)) return 0;
      if (!managerId) return 0;
      
      // The new API structure returns manager objects with total_pdf_exports
      const managerPdfData = this.pdfExportInfo.find(manager => manager.manager_id === managerId);
      
      if (managerPdfData && managerPdfData.total_pdf_exports !== undefined) {
        return managerPdfData.total_pdf_exports;
      }
      
      // Fallback: calculate from rides if total_pdf_exports is not available
      const rides = this.getPdfExportsForManager(managerId);
      return rides.reduce((total, exportInfo) => total + (exportInfo.number_of_pdf_exports || 0), 0);
    },
    
    async viewManagerDetails(manager) {
      this.selectedManager = manager;
      this.showManagerModal = true;
      // Fetch PDF export info for this specific manager
      await this.fetchPdfExportInfoForManager(manager.manager_id);
    },
    
    async fetchPdfExportInfoForManager(managerId) {
      if (!managerId) {
        this.selectedManagerPdfExports = [];
        this.selectedManagerPdfData = null;
        return;
      }
      
      this.loadingManagerPdfInfo = true;
      this.managerPdfExportError = null;
      
      try {
        const response = await rideInformationAPI.getPdfExportInfo(this.apptoken, managerId);
        console.log("PDF Export Info for Manager", managerId, ":", response);
        
        if (response.data && response.data.ServerMassageCode === 'success') {
          // The response is now an array of ride objects directly (not wrapped in manager object)
          const allRides = response.data.pdf_export_info || [];
          // Filter to only show rides that have PDF exports (number_of_pdf_exports > 0)
          this.selectedManagerPdfExports = allRides.filter(ride => ride.number_of_pdf_exports > 0);
          console.log("PDF Export Rides Data:", this.selectedManagerPdfExports);
          
          // Calculate total PDF exports from all rides
          const totalExports = this.selectedManagerPdfExports.reduce((total, ride) => total + (ride.number_of_pdf_exports || 0), 0);
          this.selectedManagerPdfData = {
            total_rides: this.selectedManagerPdfExports.length,
            total_pdf_exports: totalExports
          };
        } else {
          this.managerPdfExportError = response.data?.message || 'Failed to load PDF export information';
          this.selectedManagerPdfData = null;
          this.selectedManagerPdfExports = [];
        }
      } catch (error) {
        console.error('Failed to fetch PDF export info for manager:', error);
        this.managerPdfExportError = error.response?.data?.message || 'Failed to load PDF export information';
        this.selectedManagerPdfData = null;
        this.selectedManagerPdfExports = [];
      }
      
      this.loadingManagerPdfInfo = false;
    },
    
    getTotalPdfExportsForSelectedManager() {
      if (!this.selectedManagerPdfExports || !Array.isArray(this.selectedManagerPdfExports)) return 0;
      return this.selectedManagerPdfExports.reduce((total, ride) => total + (ride.number_of_pdf_exports || 0), 0);
    },
    
    getRideStatsFromManager(rideId) {
      // Default values
      const defaults = { number_of_days: 0, number_of_shares: 0 };
      try {
        if (!this.selectedManager) return defaults;
        const rides = this.getManagerRides(this.selectedManager);
        if (!rides || rides.length === 0) return defaults;
        const match = rides.find(r => r.ride_id === rideId);
        if (!match) return defaults;
        return {
          number_of_days: match.number_of_days || 0,
          number_of_shares: match.number_of_shares || 0
        };
      } catch (e) {
        return defaults;
      }
    },
    
    formatDate(dateString, includeTime = false) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        if (includeTime) {
          return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (error) {
        return 'Invalid Date';
      }
    },
    
    
    getFirstRideDate(manager) {
      const rides = this.getManagerRides(manager);
      if (!rides || rides.length === 0) return null;
      
      const dates = rides
        .map(ride => ride.first_shared_date)
        .filter(date => date)
        .sort();
      
      return dates.length > 0 ? dates[0] : null;
    },
    
    getLastRideDate(manager) {
      const rides = this.getManagerRides(manager);
      if (!rides || rides.length === 0) return null;
      
      const dates = rides
        .map(ride => ride.last_shared_date)
        .filter(date => date)
        .sort();
      
      return dates.length > 0 ? dates[dates.length - 1] : null;
    },
    
    getManagerRides(manager) {
      if (!manager) return [];
      
      // Check if rides are under cost_breakdown (the actual structure)
      if (manager.cost_breakdown && Array.isArray(manager.cost_breakdown)) {
        return manager.cost_breakdown;
      }
      
      // Check if rides are nested under the manager (fallback)
      if (manager.rides && Array.isArray(manager.rides)) {
        return manager.rides;
      }
      
      // If rides are at the top level, filter by manager_id (fallback)
      if (this.allRides && Array.isArray(this.allRides)) {
        const managerRides = this.allRides.filter(ride => 
          ride.manager_id === manager.manager_id
        );
        return managerRides;
      }
      
      return [];
    }
  }
};
</script>

<style scoped>
.modal-xl-custom {
  max-width: 95vw;
}

.billing-modal-body {
  font-size: 14px;
}

.billing-modal-body strong,
.billing-modal-body td,
.billing-modal-body th,
.billing-modal-body p,
.billing-modal-body label,
.billing-modal-body .alert {
  font-size: 14px;
}

.billing-modal-body h6 {
  font-size: 16px;
}

.billing-modal-body small,
.billing-modal-body .small,
.billing-modal-body .text-muted {
  font-size: 12px;
}

.billing-modal-body .badge {
  font-size: 12px;
  padding: 4px 8px;
}

.billing-modal-body .table th {
  font-size: 13px;
  padding: 10px 8px;
}

.billing-modal-body .table td {
  padding: 8px;
  vertical-align: middle;
}

.ride-manager-billing {
  min-height: 100vh;
  background-color: #f8f9fe;
  padding-top: 72px;
  font-size: 14px;
}

.ride-manager-billing p,
.ride-manager-billing td,
.ride-manager-billing th,
.ride-manager-billing label,
.ride-manager-billing .alert {
  font-size: 14px;
}

.ride-manager-billing h3 {
  font-size: 28px;
}

.ride-manager-billing h5 {
  font-size: 20px;
}

.ride-manager-billing h6 {
  font-size: 16px;
}

.ride-manager-billing small,
.ride-manager-billing .small,
.ride-manager-billing .text-muted.small {
  font-size: 12px;
}

.ride-manager-billing .badge {
  font-size: 12px;
}

.avatar-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
}

.badge-lg {
  font-size: 1rem;
  padding: 0.5rem 0.75rem;
}

.opacity-75 {
  opacity: 0.75;
}

.card {
  transition: transform 0.2s;
}

.card:hover {
  transform: translateY(-2px);
}

.table th {
  border-top: none;
  font-weight: 600;
  color: #495057;
}

.table td {
  vertical-align: middle;
}

.btn {
  transition: all 0.2s;
}

.btn:hover {
  transform: translateY(-1px);
}

/* Custom scrollbar for table */
.table-responsive {
  max-height: 600px;
  overflow-y: auto;
}

.table-responsive::-webkit-scrollbar {
  width: 8px;
}

.table-responsive::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.table-responsive::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Summary cards styling */
.card.bg-primary,
.card.bg-success,
.card.bg-info,
.card.bg-warning {
  border: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.card.bg-primary:hover,
.card.bg-success:hover,
.card.bg-info:hover,
.card.bg-warning:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

/* Modal styling */
.modal-body {
  max-height: 70vh;
  overflow-y: auto;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .ride-manager-billing {
    padding-top: 60px;
  }
  
  .table-responsive {
    font-size: 0.875rem;
  }
  
  .avatar-circle {
    width: 30px;
    height: 30px;
    font-size: 14px;
  }
  
  /* Hide some columns on mobile for better readability */
  .table th:nth-child(7),
  .table td:nth-child(7),
  .table th:nth-child(8),
  .table td:nth-child(8) {
    display: none;
  }
}

@media (max-width: 576px) {
  /* Hide more columns on very small screens */
  .table th:nth-child(2),
  .table td:nth-child(2),
  .table th:nth-child(6),
  .table td:nth-child(6) {
    display: none;
  }
}

/* Improve table spacing and readability */
.table th {
  white-space: nowrap;
  font-size: 13px;
  padding: 10px 8px;
}

.table td {
  padding: 10px 8px;
  vertical-align: middle;
}

/* Style for date columns */
.text-muted.small {
  font-size: 12px;
}

/* Badge improvements */
.badge {
  font-size: 12px;
  padding: 0.375rem 0.5rem;
}

.badge-lg {
  font-size: 14px;
  padding: 8px 12px;
}
</style>

